/* Licences — handover §14 step 8, and the reconciliation the handover says pays
   for itself.

   The register knows two things Graph does not: what a seat costs and when the
   term ends. Put those next to who actually holds the licence and the screen
   answers the only question anyone asks of it — what are we paying for that
   nobody is using.

   Assignment is counted from People.Licences rather than from the SKU's
   consumed count, because a number on its own tells you there is waste and a
   list of names tells you whose. Both come from the same nightly sync.

   Company-wide, deliberately. A licence is not bought per depot, and the depot
   scope in the header does not apply here — the dashboard's scope bar says so
   and this screen would contradict it if it filtered. */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { countdown, daysUntil, expiryTone, fmtDate, money, moneyPrecise } from '../../lib/format';
import type { ILicenceSku, IPerson } from '../../models';
import { recordHref, useOpenRecord } from '../../hooks/useOpenRecord';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Controls,
  DataTable,
  EmptyState,
  KeyValue,
  LoadError,
  Meter,
  Panel,
  SearchBox,
  Segmented,
  StatCard,
  StatGrid,
  SummaryTile,
  SummaryTiles,
  TableSkeleton,
  type Column,
} from '../ui';
import { EditDrawer } from './EditDrawer';
import { licenceFields } from '../../lib/registerFields';
import { isLeaver, visibleLicences } from '../../lib/registers';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/** A SKU with the numbers the screen actually reasons about. */
interface Row extends ILicenceSku {
  holders: IPerson[];
  /** Holders who have left. Still licensed, still billed. */
  leavers: IPerson[];
  assigned: number;
  spare: number;
  /** Annual cost of the seats nobody is sitting in. */
  idle: number;
  /** Annual cost of the seats leavers are still holding. */
  wasted: number;
}

export function LicencesScreen() {
  const svc = useServices();

  const [query, setQuery] = useState('');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<Row | null>(null);

  const qc = useQueryClient();
  const skus = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });

  const rows = useMemo<Row[]>(() => {
    const holders = new Map<string, IPerson[]>();
    for (const p of people.data ?? []) {
      for (const id of p.lic) {
        const list = holders.get(id);
        if (list) list.push(p);
        else holders.set(id, [p]);
      }
    }

    return (skus.data ?? []).map((k) => {
      const held = holders.get(k.id) ?? [];
      const leavers = held.filter(isLeaver);
      /* Counted for a per-user licence, typed for a per-device one. A device
         licence is held by nobody — Microsoft reports nought in use however many
         machines it covers — so counting holders made Intune Plan 1 Device read
         as two idle seats when both were doing their job. Everything downstream
         is the same arithmetic either way. */
      const assigned = k.basis === 'Per device' ? Math.max(0, k.seatsUsed ?? 0) : held.length;
      /* Never negative. Over-assignment is a real state — more holders than
         prepaid seats means the next bill goes up, not that there is spare. */
      const spare = Math.max(0, k.total - assigned);
      return {
        ...k,
        holders: held,
        leavers,
        assigned,
        spare,
        idle: spare * k.cost * 12,
        wasted: leavers.length * k.cost * 12,
      };
    });
  }, [skus.data, people.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((k) => {
      /* Hidden ones are out of every lens but their own. Somewhere to see them
         has to exist, though — hiding something you can never find again is
         just a slow delete. */
      if (k.hidden && lens !== 'hidden') return false;
      if (lens === 'hidden' && !k.hidden) return false;
      if (lens === 'idle' && k.spare === 0) return false;
      if (lens === 'leavers' && k.leavers.length === 0) return false;
      if (lens === 'renewing' && !(k.renews && daysUntil(k.renews) <= 90)) return false;
      if (lens === 'over' && k.assigned <= k.total) return false;
      if (!q) return true;
      return k.name.toLowerCase().includes(q) || k.short.toLowerCase().includes(q);
    });
  }, [rows, lens, query]);

  /* Every headline number counts only what is not hidden — a free SKU nobody
     uses would otherwise inflate the seat count and the idle-spend figure this
     screen exists to make honest. */
  const totals = useMemo(() => {
    const counted = visibleLicences(rows);
    return {
      seats: counted.reduce((n, k) => n + k.total, 0),
      assigned: counted.reduce((n, k) => n + Math.min(k.total, k.assigned), 0),
      idle: counted.reduce((n, k) => n + k.idle, 0),
      wasted: counted.reduce((n, k) => n + k.wasted, 0),
      spend: counted.reduce((n, k) => n + k.total * k.cost * 12, 0),
      hidden: rows.length - counted.length,
      /* Counted licences with no price. Every money figure on this screen is a
         sum, and a sum quietly leaves out what it does not know — so a screen
         reporting £40,000 of spend from nine priced SKUs and four unpriced ones
         looks exactly like a screen reporting all thirteen. The drawer already
         says so per licence, which is no use to somebody reading the headline. */
      unpriced: counted.filter((k) => k.cost === 0),
    };
  }, [rows]);

  useOpenRecord(
    rows,
    (sku, value) => sku.id === value || sku.name.toLowerCase() === value.toLowerCase(),
    setSelected,
  );

  const save = useMutation({

    meta: { ok: 'Licence saved' },
    mutationFn: (values: Record<string, unknown>) =>
      svc.licences.update(selected!.id, values as Partial<ILicenceSku>),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.licences });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setSelected(null);
    },
  });

  const loading = skus.isLoading || people.isLoading;
  const failed = [skus, people].find((q) => q.isError);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: 'Licence',
      sortBy: (k) => k.name,
      render: (k) => (
        <div className={s.person}>
          <span className={s.chip}>
            <i className="ti ti-license" aria-hidden />
          </span>
          <div>
            {/* The readable name leads. skuPartNumber is 'ENTERPRISEPACK',
                which is what Graph knows and not what anybody calls it — so it
                drops to the sub-line where it still identifies the row. */}
            <div className={s.cellMain}>{k.short || k.name}</div>
            <div className={`${s.cellSub} ${s.mono}`}>{k.name}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'seats',
      header: 'Seats',
      width: '210px',
      sortBy: (k) => (k.total > 0 ? k.assigned / k.total : 0),
      render: (k) => (
        <>
          <div className={s.cellMain}>
            {k.assigned} of {k.total}
            {k.assigned > k.total && <span className={s.cellSub}> · over-assigned</span>}
          </div>
          <Meter
            value={Math.min(k.assigned, k.total)}
            max={k.total}
            tone={k.assigned > k.total ? 'bad' : k.spare > 0 ? 'warn' : 'ok'}
          />
        </>
      ),
    },
    {
      key: 'spare',
      priority: 3,
      header: 'Spare',
      sortBy: (k) => k.spare,
      render: (k) => (k.spare === 0 ? <span className={s.cellSub}>None</span> : <b>{k.spare}</b>),
    },
    {
      key: 'idle',
      header: 'Idle a year',
      sortBy: (k) => k.idle,
      /* The number this screen exists for. Zero is good news and reads as such
         rather than as a blank cell. */
      render: (k) =>
        k.idle > 0 ? (
          <Badge tone={k.idle >= 500 ? 'bad' : 'warn'}>{money(k.idle)}</Badge>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    {
      key: 'leavers',
      header: 'Held by leavers',
      sortBy: (k) => k.leavers.length,
      /* A leaver holding a licence is not spare capacity — it is a seat being
         paid for twice over, and it does not show up in the spare count at all
         because the seat is still assigned. */
      render: (k) =>
        k.leavers.length > 0 ? (
          <Badge tone="bad">
            {k.leavers.length} · {money(k.wasted)}/yr
          </Badge>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    {
      key: 'cost',
      header: 'Per seat',
      sortBy: (k) => k.cost,
      render: (k) => (
        <>
          <div>{moneyPrecise(k.cost)}/mo</div>
          <div className={s.cellSub}>{money(k.total * k.cost * 12)}/yr all in</div>
        </>
      ),
    },
    {
      key: 'renews',
      priority: 2,
      header: 'Term ends',
      sortBy: (k) => k.renews,
      render: (k) =>
        k.renews ? (
          <Badge tone={expiryTone(k.renews)}>{fmtDate(k.renews)}</Badge>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    {
      key: 'hidden',
      header: '',
      render: (k) => (k.hidden ? <Badge tone="muted">Hidden</Badge> : null),
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Licences</h1>
          <p>
            Prepaid against assigned, what the idle seats cost a year, and who holds what.
            Seat counts sync from Microsoft 365 nightly; price and term are entered here.
          </p>
        </div>
      </div>

      {failed && <LoadError error={failed.error} what="licences" />}

      <StatGrid>
        <StatCard
          icon="ti-license"
          label="Seats assigned"
          value={loading ? '—' : `${totals.assigned}/${totals.seats}`}
          tone={totals.seats > 0 && totals.assigned / totals.seats < 0.8 ? 'warn' : 'ok'}
          foot={loading ? '' : `${totals.seats - totals.assigned} spare`}
        />
        <StatCard
          icon="ti-cash-banknote"
          label="Idle seats a year"
          value={loading ? '—' : money(totals.idle)}
          tone={totals.idle > 600 ? 'bad' : totals.idle > 0 ? 'warn' : 'ok'}
          foot={totals.idle > 0 ? 'Prepaid, unassigned' : 'Nothing sitting idle'}
        />
        <StatCard
          icon="ti-user-off"
          label="Held by leavers"
          value={loading ? '—' : money(totals.wasted)}
          tone={totals.wasted > 0 ? 'bad' : 'ok'}
          foot={totals.wasted > 0 ? 'Reclaim these first' : 'None outstanding'}
        />
        <StatCard
          icon="ti-receipt"
          label="Committed a year"
          value={loading ? '—' : money(totals.spend)}
          tone="info"
          foot="Every prepaid seat"
        />
      </StatGrid>

      {totals.unpriced.length > 0 && (
        <div className={s.ownedNote}>
          <i className="ti ti-cash-off" aria-hidden />
          <span>
            {totals.unpriced.length === 1
              ? 'One licence has no per-seat price recorded'
              : `${totals.unpriced.length} licences have no per-seat price recorded`}
            {' '}— {totals.unpriced.map((k) => k.short || k.name).join(', ')}. Every money figure
            here excludes {totals.unpriced.length === 1 ? 'it' : 'them'}, so the real spend is
            higher than it says. Prices come from your bill rather than Microsoft's list, because
            what you pay through a partner is not what Microsoft advertises.
          </span>
        </div>
      )}

      <Controls
        count={`${filtered.length} of ${rows.length - totals.hidden}${totals.hidden > 0 ? ` · ${totals.hidden} hidden` : ''}`}
      >
        <SearchBox value={query} onChange={setQuery} placeholder="Licence name…" />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'idle', label: 'Has spare' },
            { value: 'leavers', label: 'Held by leavers' },
            { value: 'over', label: 'Over-assigned' },
            { value: 'renewing', label: 'Renewing in 90 days' },
            ...(totals.hidden > 0 ? [{ value: 'hidden', label: `Hidden (${totals.hidden})` }] : []),
          ]}
        />
      </Controls>

      <Panel flush>
        {loading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={filtered}
            columns={columns}
            rowKey={(k) => k.id}
            initialSort={{ key: 'idle', dir: 'desc' }}
            onRowClick={setSelected}
            selectedKey={selected?.id}
            empty={
              <EmptyState icon="ti-license" title="No licences match">
                {lens === 'all'
                  ? 'Nothing in the licence register yet. The nightly sync fills this from /subscribedSkus.'
                  : 'Nothing under this lens — which, for every lens here, is the good outcome.'}
              </EmptyState>
            }
          />
        )}
      </Panel>

      <EditDrawer
        open={selected !== null}
        record={selected}
        fields={licenceFields()}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="licence"
        icon="ti-license"
        title={selected?.name}
        meta={
          selected && (
            <>
              <Badge tone={selected.spare > 0 ? 'warn' : 'ok'}>
                {selected.assigned} of {selected.total} assigned
              </Badge>
              {selected.leavers.length > 0 && (
                <Badge tone="bad">{selected.leavers.length} held by leavers</Badge>
              )}
              {selected.renews && (
                <Badge tone={expiryTone(selected.renews)}>{countdown(selected.renews)}</Badge>
              )}
            </>
          )
        }
        onClose={() => setSelected(null)}
        onSave={(values) => save.mutate(values)}
        saving={save.isPending}
        error={save.error}
      >
        {selected && (
          <>
            <SummaryTiles>
              <SummaryTile
                icon="ti-users"
                value={`${selected.assigned}/${selected.total}`}
                label="Seats used"
                tone={selected.assigned > selected.total ? 'bad' : selected.spare > 0 ? 'warn' : 'ok'}
              />
              <SummaryTile
                icon="ti-cash-banknote"
                value={selected.idle > 0 ? money(selected.idle) : '—'}
                label="Idle a year"
                tone={selected.idle >= 500 ? 'bad' : selected.idle > 0 ? 'warn' : 'ok'}
              />
              <SummaryTile
                icon="ti-user-off"
                value={selected.leavers.length}
                label="Held by leavers"
                tone={selected.leavers.length > 0 ? 'bad' : 'ok'}
              />
              <SummaryTile
                icon="ti-receipt"
                value={moneyPrecise(selected.cost)}
                label="Per seat, a month"
                /* Zero here is why every money figure on the screen reads zero,
                   so it is the finding rather than a neutral value. */
                tone={selected.cost === 0 ? 'warn' : 'muted'}
              />
            </SummaryTiles>

            <KeyValue
              rows={[
                { key: 'Prepaid seats', value: String(selected.total) },
                { key: 'Assigned', value: String(selected.assigned) },
                { key: 'Spare', value: String(selected.spare) },
                { key: 'Idle a year', value: selected.idle > 0 ? money(selected.idle) : '—' },
              ]}
            />

            {selected.cost === 0 && (
              <div className={s.ownedNote}>
                <i className="ti ti-cash-off" aria-hidden />
                <span>
                  No per-seat price recorded, so this licence reports as costing nothing however
                  many seats sit idle. It is the one field on this screen worth filling in first.
                </span>
              </div>
            )}

            {selected.leavers.length > 0 && (
              <div>
                <div className={s.sectionLabel}>Reclaim these first</div>
                <div className={s.rows}>
                  {selected.leavers.map((p) => (
                    <Link
                      key={p.id}
                      to={recordHref('/people', p.id)}
                      className={`${s.row} ${s.rowFlush}`}
                    >
                      <span className={s.rowIcon}>
                        <i className="ti ti-user-off" aria-hidden />
                      </span>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{p.name}</div>
                        <div className={s.rowSub}>{p.upn}</div>
                      </div>
                      <Badge tone="bad">Leaver</Badge>
                      <i className="ti ti-chevron-right" aria-hidden />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className={s.sectionLabel}>Who holds it</div>
              <div className={s.rows}>
                {selected.holders
                  .filter((p) => p.status !== 'Leaver')
                  .map((p) => (
                    <div key={p.id} className={`${s.row} ${s.rowFlush}`}>
                      <span className={s.rowIcon}>
                        <i className="ti ti-user" aria-hidden />
                      </span>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{p.name}</div>
                        <div className={s.rowSub}>
                          {p.title} · {svc.depots.name(p.loc)}
                        </div>
                      </div>
                    </div>
                  ))}
                {selected.holders.length === 0 && (
                  <span className={s.hint}>
                    Nobody holds this. Every prepaid seat on it is idle spend.
                  </span>
                )}
              </div>
            </div>

            <div className={s.ownedNote}>
              <i className="ti ti-refresh" aria-hidden />
              <span>
                Seat counts and the SKU name come from the nightly sync and are not editable here.
                The price, the term and the short name are {PRODUCT.name}'s — and are the reason this
                screen can put a number on the waste at all.
              </span>
            </div>
          </>
        )}
      </EditDrawer>
    </>
  );
}
